import cn from 'classnames';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  ConsultationStatus,
  Document,
  RecordingMode,
} from '@/shared/api/transcriptor';
import { Button } from '@/shared/ui';

import { canReorderConsultation } from '@/entities/transcriptor';

import { i18n } from '../../config';
import { useOrderableDocuments } from '../../hooks';
import { AddDocumentsModal } from '../AddDocumentsModal';

import styles from './AddDocumentsButton.module.scss';

type AddDocumentsButtonProps = {
  consultationId: string;
  documents: Document[];
  schemaId?: string;
  status?: ConsultationStatus;
  recordingMode?: RecordingMode;
  className?: string;
};

export const AddDocumentsButton: FC<AddDocumentsButtonProps> = (props) => {
  const {
    consultationId,
    documents,
    schemaId,
    status,
    recordingMode,
    className,
  } = props;

  const { formatMessage } = useIntl();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { hasLoadError, orderableDocuments, retryLoad } = useOrderableDocuments(
    { documents, schemaId },
  );

  const handleClick = () => {
    if (hasLoadError) {
      retryLoad();
      return;
    }

    setIsModalOpen(true);
  };

  if (
    !canReorderConsultation({ status, recordingMode }) ||
    (!hasLoadError && orderableDocuments.length === 0)
  ) {
    return null;
  }

  return (
    <>
      <Button
        variant="tertiary"
        size="large"
        icon="plus"
        aria-label={formatMessage(
          i18n.consultationOptionsMessages.addDocumentsButton,
        )}
        className={cn(styles.button, className)}
        onClick={handleClick}
      />

      {!hasLoadError && (
        <AddDocumentsModal
          isOpen={isModalOpen}
          consultationId={consultationId}
          documents={orderableDocuments}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
};
